import React, { createContext, useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SessionContextProviderProps } from "../interfaces/ContextInterface";

interface SessionContextType {
  isLoggedIn: boolean;
  login: () => void;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);
const AUTH_STORAGE_KEYS = [
  "rememberMe",
  "token",
  "user",
  "isLoggedIn",
  "ug_cart",
  "ug_pending_cart_checkout",
  "user_session",
  "ug_user",
  "refreshToken",
] as const;

const AUTH_SYNC_KEY = "ug:admin:auth:event";
const LOGOUT_MARKER_COOKIE = "ug_logout_sync";

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() ?? null;
  return null;
};

const getLegacySharedDomain = (): string | undefined => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") {
    return undefined;
  }
  const parts = host.split(".");
  if (parts.length < 2) {
    return undefined;
  }
  return `.${parts.slice(-2).join(".")}`;
};

const getSecureCookieSuffix = () =>
  window.location.protocol === "https:" ? "; Secure" : "";
const hasLogoutMarker = () => getCookie(LOGOUT_MARKER_COOKIE) === "1";
const clearAuthState = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

const setLogoutMarker = () => {
  const secure = getSecureCookieSuffix();
  document.cookie = `${LOGOUT_MARKER_COOKIE}=1; path=/; max-age=31536000; SameSite=Lax${secure};`;
};
const clearLogoutMarker = () => {
  const secure = getSecureCookieSuffix();
  // Clear host-only marker
  document.cookie = `${LOGOUT_MARKER_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax${secure};`;
  // Defensively clear any legacy parent-domain marker left behind from older builds
  const legacyDomain = getLegacySharedDomain();
  if (legacyDomain) {
    document.cookie = `${LOGOUT_MARKER_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax${secure}; domain=${legacyDomain};`;
  }
};
const broadcastAuthEvent = (type: "login" | "logout") => {
  try {
    localStorage.setItem(
      AUTH_SYNC_KEY,
      JSON.stringify({ type, at: Date.now() }),
    );
  } catch (_error) {}
};

const getLogoutUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || "";
  if (!apiUrl) {
    return "/logout";
  }

  if (/\/graphql\/?$/i.test(apiUrl)) {
    return apiUrl.replace(/\/graphql\/?$/i, "/logout");
  }

  return apiUrl.replace(/\/+$/, "") + "/logout";
};

/**
 * Resolves the active token and user from storage.
 *
 * Priority:
 *  1. sessionStorage  – always preferred (already in-tab)
 *  2. localStorage    – set when "Remember Me" was checked; synced into
 *                       sessionStorage so the rest of the app works uniformly.
 */
const resolveSession = (): { token: string | null; user: string | null } => {
  if (hasLogoutMarker()) {
    clearAuthState();
    return { token: null, user: null };
  }

  // Always check localStorage first for stable cross-tab support
  let token = localStorage.getItem("token") || sessionStorage.getItem("token");
  let user = localStorage.getItem("user") || sessionStorage.getItem("user");

  if (token && user) {
    // Sync into sessionStorage to ensure UI remains responsive in current tab
    if (!sessionStorage.getItem("token")) sessionStorage.setItem("token", token);
    if (!sessionStorage.getItem("user")) sessionStorage.setItem("user", user);
  }

  return { token, user };
};

const SessionContextProvider: React.FC<SessionContextProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialise synchronously so the first render already reflects auth state
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
    const { token, user } = resolveSession();
    return !!(token && user);
  });
  
  useEffect(() => {
    const syncSessionState = () => {
      const { token, user } = resolveSession();
      setIsLoggedIn(!!(token && user));
    };
    const handleStorageEvent = (event: StorageEvent) => {
      if (
        event.key &&
        event.key !== AUTH_SYNC_KEY &&
        !AUTH_STORAGE_KEYS.includes(event.key as (typeof AUTH_STORAGE_KEYS)[number])
      ) {
        return;
      }
      syncSessionState();
    };
    window.addEventListener("storage", handleStorageEvent);
    window.addEventListener("focus", syncSessionState);
    return () => {
      window.removeEventListener("storage", handleStorageEvent);
      window.removeEventListener("focus", syncSessionState);
    };
  }, []);

  useEffect(() => {
    const normalizePath = (path: string): string => {
      const trimmed = path.replace(/\/+$/, "");
      return trimmed === "" ? "/" : trimmed.toLowerCase();
    };

    const { token, user } = resolveSession();
    const loggedIn = !!(token && user);
    setIsLoggedIn(loggedIn);

    const pathKey = normalizePath(location.pathname);
    const isPublicRoute =
      pathKey === "/" ||
      pathKey === "/signup" ||
      pathKey === "/forgotpassword" ||
      /^\/resetpassword\/[^/]+$/.test(pathKey);

    if (loggedIn && pathKey === "/") {
      navigate("/dashboard", { replace: true });
    } else if (!loggedIn && !isPublicRoute) {
      navigate("/", { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navigate]);

  const login = () => {
    clearLogoutMarker();
    setIsLoggedIn(true);
    broadcastAuthEvent("login");
  };

  const logout = async () => {
    setLogoutMarker();
    clearAuthState();

    try {
      await fetch(getLogoutUrl(), {
        method: "POST",
        credentials: "include",
      });
    } catch (_error) {}

    setIsLoggedIn(false);
    broadcastAuthEvent("logout");
    navigate("/", { replace: true });
  };

  return (
    <SessionContext.Provider value={{ isLoggedIn, login, logout }}>
      {children}
    </SessionContext.Provider>
  );
};

const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within SessionContextProvider");
  }
  return context;
};

export { SessionContextProvider, useSession };
